import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Send, Paperclip, Phone, Video, ArrowLeft, User, Building2, 
  FileText, Image as ImageIcon, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const WS_API = `${WS_URL}/api`;

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initiate a video/audio call
  const initiateCall = useCallback(async (callType = 'audio') => {
    try {
      const response = await axios.post(`${API}/calls/initiate`, {
        chat_id: chatId,
        call_type: callType
      }, { withCredentials: true });
      
      // Navigate to call page
      navigate(`/call/${response.data.call_id}?initiator=true&chatId=${chatId}`);
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error('Failed to initiate call');
    }
  }, [chatId, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchChat = async () => {
      try {
        const [chatRes, messagesRes] = await Promise.all([
          axios.get(`${API}/chats/${chatId}`, { withCredentials: true }),
          axios.get(`${API}/chats/${chatId}/messages`, { withCredentials: true })
        ]);
        setChat(chatRes.data);
        setMessages(messagesRes.data);
      } catch (error) {
        console.error('Error fetching chat:', error);
        toast.error('Failed to load chat');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchChat();

    // Connect WebSocket (using /api prefix for ingress routing)
    const ws = new WebSocket(`${WS_API}/ws/chat/${chatId}`);
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      setMessages(prev => [...prev, message]);
    };
    wsRef.current = ws;

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [chatId, isAuthenticated, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/chats/${chatId}/messages`, {
        content: newMessage,
        message_type: 'text'
      }, { withCredentials: true });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadRes = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const messageType = file.type.startsWith('image/') ? 'image' : 'document';
      
      await axios.post(`${API}/chats/${chatId}/messages`, {
        content: file.name,
        message_type: messageType,
        file_url: uploadRes.data.file_url,
        file_name: uploadRes.data.file_name
      }, { withCredentials: true });

      toast.success('File sent');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCallRequest = async () => {
    const phone = prompt('Enter your phone number for callback:');
    if (!phone) return;

    try {
      await axios.post(`${API}/chats/${chatId}/call-request`, {
        phone_number: phone
      }, { withCredentials: true });
      toast.success('Call request sent');
    } catch (error) {
      console.error('Error requesting call:', error);
      toast.error('Failed to request call');
    }
  };

  const isOwnMessage = (msg) => msg.sender_id === user?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="rounded-none"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
            {user?.role === 'agency' ? (
              <User className="w-5 h-5 text-slate-600" />
            ) : (
              <Building2 className="w-5 h-5 text-slate-600" />
            )}
          </div>
          <div>
            <h1 className="font-display font-bold text-slate-900">
              {user?.role === 'agency' ? chat?.user_name : chat?.agency_name}
            </h1>
            <p className="text-xs text-slate-500">
              {user?.role === 'agency' ? 'Customer' : 'Detective Agency'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => initiateCall('audio')}
            className="rounded-none border-slate-200"
            data-testid="audio-call-btn"
          >
            <Phone className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => initiateCall('video')}
            className="rounded-none border-slate-200"
            data-testid="video-call-btn"
          >
            <Video className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${isOwnMessage(msg) ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] ${
                  isOwnMessage(msg)
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-slate-200'
                } p-3`}
              >
                {!isOwnMessage(msg) && (
                  <p className="text-xs font-medium text-slate-500 mb-1">{msg.sender_name}</p>
                )}
                
                {msg.message_type === 'text' && (
                  <p className={isOwnMessage(msg) ? 'text-white' : 'text-slate-900'}>
                    {msg.content}
                  </p>
                )}
                
                {msg.message_type === 'image' && msg.file_url && (
                  <div>
                    <img 
                      src={msg.file_url} 
                      alt={msg.file_name || 'Image'} 
                      className="max-w-full rounded cursor-pointer"
                      onClick={() => window.open(msg.file_url, '_blank')}
                    />
                    <p className="text-xs mt-1 opacity-70">{msg.file_name}</p>
                  </div>
                )}
                
                {msg.message_type === 'document' && msg.file_url && (
                  <a 
                    href={msg.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 ${isOwnMessage(msg) ? 'text-white' : 'text-slate-900'}`}
                  >
                    <FileText className="w-5 h-5" />
                    <span className="underline">{msg.file_name || 'Document'}</span>
                  </a>
                )}
                
                {msg.message_type === 'call_request' && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{msg.content}</span>
                  </div>
                )}
                
                <p className={`text-xs mt-1 ${isOwnMessage(msg) ? 'text-slate-300' : 'text-slate-400'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t border-slate-200 p-4">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-none border-slate-200"
            data-testid="attach-file-btn"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Paperclip className="w-4 h-4" />
            )}
          </Button>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 rounded-none border-slate-200"
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            data-testid="message-input"
          />
          
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
            data-testid="send-message-btn"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
